import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Calendar } from "./ui/calendar";
import { TimePickerField } from "./TimePickerField";

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseLocalDateOnly(value) {
  if (!value) return null;
  const raw = String(value).trim();
  // Must be exactly YYYY-MM-DD — ISO datetimes (with T/time) are not date-only.
  if (!DATE_ONLY_RE.test(raw)) return null;
  const [year, month, day] = raw.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatMilestoneDateTime(iso) {
  if (!iso) return "";
  const d = parseLocalDateOnly(iso) || new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function formatMilestoneDueDisplay(iso) {
  if (!iso) return "";
  const localDate = parseLocalDateOnly(iso);
  if (localDate) {
    return localDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (
    (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0)
    || (d.getHours() === 23 && d.getMinutes() === 59 && d.getSeconds() === 0)
  ) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  return formatMilestoneDateTime(iso);
}

const parseDateValue = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const toDateValue = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseIsoToDateTime = (iso) => {
  if (!iso) return { date: "", time: "" };
  const localDate = parseLocalDateOnly(iso);
  if (localDate) {
    return { date: toDateValue(localDate), time: "" };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const date = toDateValue(d);
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
    return { date, time: "" };
  }
  return {
    date,
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
};

const parseIsoToEndDateTime = (iso) => {
  if (!iso) return { date: "", time: "" };
  const localDate = parseLocalDateOnly(iso);
  if (localDate) {
    return { date: toDateValue(localDate), time: "" };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const date = toDateValue(d);
  if (d.getHours() === 23 && d.getMinutes() === 59 && d.getSeconds() === 0) {
    return { date, time: "" };
  }
  return {
    date,
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
};

/** Matches Messages thread topic dropdown surface (`PopoverContent` + `t-glass` on `MessagesPage`). */
const INLINE_PLANNING_DATE_PANEL_CLASS =
  "mt-3 max-w-full overflow-x-auto overflow-hidden rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] p-0 !shadow-none text-[15px] font-light font-[inherit] text-[var(--t-text)] outline-none transition-[border-color] duration-[160ms] ease-out t-glass [-webkit-overflow-scrolling:touch]";

/** Range picker for immersion / in-development milestones — panel expands inline (no floating popover). */
export const InlineDateRangePicker = forwardRef(function InlineDateRangePicker(
  { startIso, endIso, onChange, testId, label, className = "" },
  ref,
) {
  const [expanded, setExpanded] = useState(false);
  const [calTick, setCalTick] = useState(0);
  const [rangeSel, setRangeSel] = useState({ from: undefined, to: undefined });
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");

  useImperativeHandle(ref, () => ({
    /** Uncommitted calendar selection when the panel is open (Save can flush without a separate Apply). */
    getPendingRange() {
      if (!expanded || !rangeSel?.from) return null;
      return {
        from: toDateValue(rangeSel.from),
        to: rangeSel.to ? toDateValue(rangeSel.to) : "",
        fromTime,
        toTime,
      };
    },
  }), [expanded, rangeSel, fromTime, toTime]);

  const display = useMemo(() => {
    if (!startIso && !endIso) return label || "Set date";
    if (startIso && endIso) return `${formatMilestoneDueDisplay(startIso)} – ${formatMilestoneDueDisplay(endIso)}`;
    if (startIso) return formatMilestoneDueDisplay(startIso);
    return formatMilestoneDueDisplay(endIso);
  }, [startIso, endIso, label]);

  useEffect(() => {
    if (!expanded) return;
    setRangeSel({
      from: startIso ? parseDateValue(startIso) : undefined,
      to: endIso ? parseDateValue(endIso) : undefined,
    });
    setFromTime(startIso ? parseIsoToDateTime(startIso).time : "");
    setToTime(endIso ? parseIsoToEndDateTime(endIso).time : "");
  }, [expanded, startIso, endIso]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  const resetRangeDraft = () => {
    setRangeSel({ from: undefined, to: undefined });
    setFromTime("");
    setToTime("");
    setCalTick((x) => x + 1);
  };

  return (
    <div className={`mb-4 ${className}`.trim()}>
      <button
        type="button"
        className={`t-meta text-xs hover:text-[var(--t-text)] ${expanded ? "text-[var(--t-text)]" : ""}`}
        data-testid={testId}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {display}
      </button>
      {expanded && (
        <div className={INLINE_PLANNING_DATE_PANEL_CLASS} data-testid={`${testId}-panel`}>
          <div className="flex items-center justify-between gap-3 border-b border-[var(--t-border)] p-3">
            <div className="label-eyebrow">{label || "Date"}</div>
            <button
              type="button"
              className="t-btn-ghost px-2 py-1 text-xs"
              onClick={() => {
                resetRangeDraft();
                onChange({ from: "", to: "", fromTime: "", toTime: "" });
                setExpanded(false);
              }}
              data-testid={`${testId}-clear`}
            >
              Clear
            </button>
          </div>
          <Calendar
            key={`range-cal-${calTick}`}
            mode="range"
            selected={rangeSel}
            onSelect={(next) => {
              const nextRange = next || { from: undefined, to: undefined };
              setRangeSel(nextRange);
            }}
            numberOfMonths={1}
            initialFocus
            className="w-full min-w-0 p-4"
          />
          <div className="flex flex-row flex-nowrap items-end gap-4 border-t border-[var(--t-border)] p-3">
            <div className="min-w-0 flex-1">
              <label className="label-eyebrow mb-1 block">Start time</label>
              <TimePickerField
                value={fromTime}
                onChange={setFromTime}
                popoverTitle="Start time"
                data-testid={`${testId}-start-time`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <label className="label-eyebrow mb-1 block">End time</label>
              <TimePickerField
                value={toTime}
                onChange={setToTime}
                popoverTitle="End time"
                data-testid={`${testId}-end-time`}
              />
            </div>
          </div>
          <div className="flex justify-end border-t border-[var(--t-border)] p-3">
            <button
              type="button"
              className="t-btn-primary min-h-[44px] px-5 text-sm"
              disabled={!rangeSel?.from}
              onClick={() => {
                const fromStr = rangeSel.from ? toDateValue(rangeSel.from) : "";
                const toStr = rangeSel.to ? toDateValue(rangeSel.to) : "";
                if (!fromStr) return;
                onChange({ from: fromStr, to: toStr, fromTime, toTime });
                setExpanded(false);
              }}
              data-testid={`${testId}-apply`}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

/** Build stored value from local YYYY-MM-DD; empty time stores date-only. */
export function localDateTimeToIso(dateStr, timeStr) {
  if (!dateStr) return null;
  const dateOnly = dateStr.slice(0, 10);
  if (!DATE_ONLY_RE.test(dateOnly)) return null;
  const empty = timeStr == null || String(timeStr).trim() === "";
  if (empty) return dateOnly;
  const [y, mo, d] = dateOnly.split("-").map((n) => Number(n));
  const [hh, mm] = String(timeStr).slice(0, 5).split(":").map((n) => Number(n));
  return new Date(y, mo - 1, d, Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0).toISOString();
}
